import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';

interface LegalPageProps {
  title: string;
  bodyHtml: string;
}

/**
 * Public, unauthenticated legal page shell (Privacy Policy / Terms of Service).
 *
 * Renders the shared site design language (dark slate canvas, brand gradients,
 * Inter font) around the same HTML content that is emitted as static
 * privacy.html / terms.html for crawlers via scripts/gen-legal-static.mjs.
 */
export default function LegalPage({ title, bodyHtml }: LegalPageProps) {
  useEffect(() => {
    document.title = `${title} — Super AI Kingdom`;
  }, [title]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Top bar */}
      <header className="border-b border-white/5 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2" aria-label="Super AI Kingdom home">
            <Logo size="md" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
          >
            <ArrowLeft size={15} />
            Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-brand-500/10 via-purple-600/5 to-transparent p-8 sm:p-12">
          <div className="absolute left-1/2 top-0 h-[160px] w-[360px] -translate-x-1/2 rounded-full bg-brand-500/20 blur-[80px]" />
          <h1 className="relative text-3xl font-bold sm:text-4xl">{title}</h1>
          <p className="relative mt-3 text-sm text-slate-400">
            Super AI Kingdom — <a className="text-brand-300 hover:text-brand-200" href="mailto:superaikingdom@gmail.com">superaikingdom@gmail.com</a>
          </p>
        </div>

        <article className="legal-article mt-10 text-sm leading-relaxed text-slate-300">
          {/* Content is trusted, static, in-repo legal copy. */}
          <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
        </article>

        <div className="mt-12 flex flex-wrap items-center gap-4 border-t border-white/5 pt-6 text-sm text-slate-400">
          <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
          <span className="text-slate-700">•</span>
          <Link to="/terms" className="hover:text-white">Terms of Service</Link>
          <span className="text-slate-700">•</span>
          <Link to="/" className="hover:text-white">Home</Link>
        </div>
      </main>
    </div>
  );
}
